import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
	changeSessionState,
	selectCurrentSessionState,
	selectSessionId,
	StartedState
} from '../../app/slices/session.slice';
import { selectBankIntegrationVendor } from '../../app/slices/configuration.slice';
import { getSessionService } from '../../services/service.factory';

const OBSERVATION_REQUEST_DELAY = 5000;

export const usePolling = () => {
	const dispatch = useAppDispatch();
	const isFetchingRef = useRef<boolean>(false);

	const currentSessionState = useAppSelector(selectCurrentSessionState);
	const sessionId = useAppSelector(selectSessionId);
	const vendor = useAppSelector(selectBankIntegrationVendor);

	const isPollingEnabled = currentSessionState && isPollingRequired(currentSessionState);

	useEffect(() => {
		if (!isPollingEnabled) return;

		async function fetchSessionState() {
			// Prevent multiple fetches if one is already in progress
			if (isFetchingRef.current) return;

			try {
				isFetchingRef.current = true;
				const service = getSessionService(vendor || 'KLARNA');
				const nextSessionState = await service.getSessionState(sessionId as string);
				dispatch(changeSessionState(nextSessionState));
			} catch (error) {
				console.error('Error fetching session state:', error);
			} finally {
				isFetchingRef.current = false;
			}
		}

		const intervalID = setInterval(fetchSessionState, OBSERVATION_REQUEST_DELAY);

		return () => {
			clearInterval(intervalID);
		};
	}, [isPollingEnabled, sessionId, vendor, dispatch]);
};

const isPollingRequired = (state: StartedState): boolean => {
	return 'polling' in state && state.polling === true;
};
